import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Code2,
  BarChart3,
  Users,
  Phone,
  Network,
  Database,
  Presentation,
  Globe,
  CheckCircle2,
  ChevronRight,
} from "lucide-react";
import { SERVICES } from "@/lib/siteData";

const iconMap = {
  Code2,
  BarChart3,
  Users,
  Phone,
  Network,
  Database,
  PresentationChart: Presentation,
  Globe,
};

export default function Services() {
  const { slug } = useParams();
  const active = useMemo(() => {
    return SERVICES.find((s) => s.slug === slug) || SERVICES[0];
  }, [slug]);

  const ActiveIcon = iconMap[active.icon] || Code2;

  return (
    <div data-testid="services-page">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg opacity-70 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24">
          <p className="vd-eyebrow">Services</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-slate-900 max-w-4xl">
            End-to-end research execution,{" "}
            <span className="text-teal-700">delivered as one partnership</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed">
            Eight specialized capabilities. One coordinated team. Every stage of
            your research lifecycle, run with rigour, transparency, and care.
          </p>
        </div>
      </section>

      {/* MAIN: SIDEBAR + DETAIL */}
      <section>
        <div className="vd-container py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <p className="vd-eyebrow">All Services</p>
              <nav className="mt-5 border border-slate-200 rounded-md overflow-hidden">
                {SERVICES.map((s, i) => {
                  const Icon = iconMap[s.icon] || Code2;
                  const isActive = s.slug === active.slug;
                  return (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`service-nav-${s.slug}`}
                      className={`flex items-center gap-3 px-5 py-4 border-b border-slate-200 last:border-b-0 transition-colors ${
                        isActive
                          ? "bg-slate-900 text-white"
                          : "bg-white hover:bg-slate-50 text-slate-800"
                      }`}
                    >
                      <Icon
                        className={`h-4 w-4 ${
                          isActive ? "text-teal-400" : "text-teal-700"
                        }`}
                      />
                      <span className="text-[14px] font-medium flex-1">
                        {s.title}
                      </span>
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${
                          isActive
                            ? "text-orange-400 translate-x-0.5"
                            : "text-slate-400"
                        }`}
                      />
                    </Link>
                  );
                })}
              </nav>
            </div>
          </aside>

          {/* Detail */}
          <motion.div
            key={active.slug}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="lg:col-span-8"
          >
            <div className="border border-slate-200 rounded-md p-8 md:p-12 bg-white">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-md border border-slate-200 flex items-center justify-center text-teal-700">
                  <ActiveIcon className="h-5 w-5" />
                </div>
                <p className="vd-eyebrow">Service Detail</p>
              </div>
              <h2 className="mt-6 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
                {active.title}
              </h2>
              <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
                {active.long}
              </p>

              <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <p className="vd-eyebrow">What We Deliver</p>
                  <ul className="mt-4 space-y-3">
                    {active.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-teal-700 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-800 text-[15px]">{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="vd-eyebrow">Capabilities</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {active.capabilities.map((c) => (
                      <span
                        key={c}
                        className="text-[13px] text-slate-800 border border-slate-200 rounded-full px-3 py-1.5 bg-white hover:border-teal-600 transition-colors"
                      >
                        {c}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <p className="font-display text-lg text-slate-900">
                  {active.cta}
                </p>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${active.slug}`}
                  className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-6 py-3 rounded-md transition-colors shadow-sm"
                >
                  Request a Consultation <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>

            {/* Other services list */}
            <div className="mt-14">
              <p className="vd-eyebrow">Continue Exploring</p>
              <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {SERVICES.filter((s) => s.slug !== active.slug)
                  .slice(0, 4)
                  .map((s) => {
                    const Icon = iconMap[s.icon] || Code2;
                    return (
                      <Link
                        key={s.slug}
                        to={`/services/${s.slug}`}
                        className="group border border-slate-200 rounded-md p-5 bg-white hover:border-teal-600 hover:-translate-y-0.5 transition-all duration-300"
                      >
                        <Icon className="h-5 w-5 text-teal-700" />
                        <div className="mt-3 font-display text-base font-semibold text-slate-900">
                          {s.title}
                        </div>
                        <div className="mt-1 text-sm text-slate-600 leading-snug">
                          {s.short}
                        </div>
                      </Link>
                    );
                  })}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
